import dayjs from "dayjs";
import {
  TimeOffRequest,
  TimeOffRequestEntity,
  TimeOffRequestDayjsEntity,
  TimeOffRequestRequest,
  TimeOffRequestResponse,
  CollaboratorTimeOffOverview,
  TimeOffType,
} from "../shared/types/time-off-request.types";
import { BaseServiceLegacy } from "./base.service.legacy";
import { toMexicoStartOfDay } from "../shared/helpers";
import { ApiResponseList, ApiResponseOne, AxiosParams } from "../shared/types";
import hvpAuthenticatedApi from "../api/hvpAuthenticatedApi";

const API_URL = "/time-off-requests";
export class TimeOffRequestService extends BaseServiceLegacy<
  TimeOffRequestResponse,
  TimeOffRequestRequest
> {
  constructor() {
    super(API_URL);
  }

  /**
   * Normalize the picked `requestedDays` to Mexico start-of-day before sending.
   * Date pickers produce browser-local midnights; without this, an admin working
   * from a non-Mexico timezone (e.g. Spain) would store the day shifted by one.
   * See GH#412 timezone fix.
   */
  private withMexicoRequestedDays(
    resource: TimeOffRequestRequest
  ): TimeOffRequestRequest {
    const requestedDays = (resource?.requestedDays ?? []) as Array<
      string | Date
    >;
    if (requestedDays.length === 0) return resource;
    return {
      ...resource,
      requestedDays: requestedDays.map((day) =>
        toMexicoStartOfDay(day).toISOString()
      ),
    };
  }

  async create(resource: TimeOffRequestRequest) {
    return super.create(this.withMexicoRequestedDays(resource));
  }

  async update(id: string, resource: TimeOffRequestRequest) {
    return super.update(id, this.withMexicoRequestedDays(resource));
  }

  public async getCollaboratorTimeOffOverview(
    collaboratorId: string,
    params?: AxiosParams
  ): Promise<ApiResponseOne<CollaboratorTimeOffOverview>> {
    const response = await hvpAuthenticatedApi.get<
      ApiResponseOne<CollaboratorTimeOffOverview>
    >(`${this.API_URL}/collaborators/time-off-overview/${collaboratorId}`, {
      params,
    });
    return response.data;
  }

  public async getCollaboratorsTimeOffOverview(
    params?: AxiosParams
  ): Promise<ApiResponseList<CollaboratorTimeOffOverview>> {
    const response = await hvpAuthenticatedApi.get<
      ApiResponseList<CollaboratorTimeOffOverview>
    >(`${this.API_URL}/collaborators/time-off-overview`, { params });
    return response.data;
  }

  public transformResponseToEntity(
    response: TimeOffRequestRequest
  ): TimeOffRequestEntity {
    return {
      ...response,
      approvedAt: response.approvedAt
        ? new Date(response.approvedAt)
        : undefined,
      requestedAt: new Date(response.requestedAt),
      requestedDays: response.requestedDays.map((day) => new Date(day)),
    };
  }

  public transformEntityToMxEntity(
    timeOffRequest: TimeOffRequestEntity
  ): TimeOffRequestDayjsEntity {
    return {
      ...timeOffRequest,
      requestedDays: timeOffRequest.requestedDays.map((day) =>
        toMexicoStartOfDay(day)
      ),
      approvedAt: timeOffRequest.approvedAt
        ? toMexicoStartOfDay(timeOffRequest.approvedAt)
        : undefined,
      requestedAt: toMexicoStartOfDay(timeOffRequest.requestedAt),
    };
  }
  public transformResponseToMxEntity(
    response: TimeOffRequestRequest
  ): TimeOffRequestDayjsEntity {
    return this.transformEntityToMxEntity(
      this.transformResponseToEntity(response)
    );
  }

  public isTimeOffTypeVacation(timeOffType: TimeOffType): boolean {
    return (
      timeOffType === TimeOffType.Vacation ||
      timeOffType === TimeOffType.PersonalLeave
    );
  }
}
